/**
 * On-device liveness for web check-in using MediaPipe Face Landmarker.
 *
 * The person follows a short random challenge issued by the server (e.g.
 * "turn left", then "blink"). This defeats a held-up photo and casual replays;
 * it is a *deterrent*, not the security boundary — the server re-verifies
 * identity from the submitted frame, binds the attempt to a single-use nonce,
 * and checks geofence / timing. See docs/05-face-attendance.md.
 *
 * Nothing leaves the browser except the final frame(s) and a small evidence
 * summary; landmarks are computed locally.
 */
import type { FaceLandmarker, FaceLandmarkerResult } from '@mediapipe/tasks-vision';

const BASE = (import.meta.env.VITE_MEDIAPIPE_BASE as string | undefined) ?? 'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.35/wasm';
const MODEL = (import.meta.env.VITE_FACE_MODEL_URL as string | undefined) ?? 'https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task';

let landmarker: Promise<FaceLandmarker> | null = null;

export function loadLandmarker(): Promise<FaceLandmarker> {
  landmarker ??= (async () => {
    const { FaceLandmarker, FilesetResolver } = await import('@mediapipe/tasks-vision');
    const fileset = await FilesetResolver.forVisionTasks(BASE);
    return FaceLandmarker.createFromOptions(fileset, {
      baseOptions: { modelAssetPath: MODEL, delegate: 'GPU' },
      runningMode: 'VIDEO',
      numFaces: 2,
      outputFaceBlendshapes: true,
    });
  })().catch((e) => {
    landmarker = null;
    throw e;
  });
  return landmarker;
}

export type Step = 'turn_left' | 'turn_right' | 'blink' | 'look_up' | 'smile';

export const STEP_PROMPT: Record<Step, string> = {
  turn_left: 'Turn your head to the left',
  turn_right: 'Turn your head to the right',
  blink: 'Blink slowly',
  look_up: 'Look up a little',
  smile: 'Give us a smile',
};

export interface FaceSignal {
  faces: number;
  /** 0.5 ≈ facing camera; >0.5 = turned to the person's left (raw camera frame). */
  yaw: number;
  /** Lower = looking up. */
  pitch: number;
  blink: number;
  smile: number;
  /** Face roughly centred and large enough. */
  framed: boolean;
}

export function readSignal(r: FaceLandmarkerResult): FaceSignal {
  const faces = r.faceLandmarks.length;
  const lm = r.faceLandmarks[0];
  if (!lm) return { faces, yaw: 0.5, pitch: 0.45, blink: 0, smile: 0, framed: false };
  const nose = lm[1]!;
  const left = lm[234]!;
  const right = lm[454]!;
  const top = lm[10]!;
  const chin = lm[152]!;
  const eyeL = lm[33]!;
  const eyeR = lm[263]!;
  const width = Math.abs(right.x - left.x);
  const yaw = width > 0 ? (nose.x - Math.min(left.x, right.x)) / width : 0.5;
  const eyeY = (eyeL.y + eyeR.y) / 2;
  const pitch = (nose.y - eyeY) / Math.max(chin.y - eyeY, 1e-3);
  const cat = Object.fromEntries((r.faceBlendshapes[0]?.categories ?? []).map((c) => [c.categoryName, c.score]));
  const blink = Math.min(cat.eyeBlinkLeft ?? 0, cat.eyeBlinkRight ?? 0);
  const smile = ((cat.mouthSmileLeft ?? 0) + (cat.mouthSmileRight ?? 0)) / 2;
  const cx = (left.x + right.x) / 2;
  const cy = (top.y + chin.y) / 2;
  const framed = width > 0.22 && width < 0.75 && Math.abs(cx - 0.5) < 0.2 && Math.abs(cy - 0.5) < 0.22;
  return { faces, yaw, pitch, blink, smile, framed };
}

/**
 * Facing the camera, head level, eyes open: the kind of frame the server
 * identifies best from (the last challenge step may leave the head turned).
 */
export const isFrontal = (s: FaceSignal) => s.faces === 1 && s.framed && Math.abs(s.yaw - 0.5) < 0.1 && s.pitch >= 0.3 && s.blink < 0.35;

/** Per-step detector with simple state so a blink must close *then* open. */
export function stepDetector(step: Step) {
  let phase = 0;
  return (s: FaceSignal): boolean => {
    if (s.faces !== 1) return false;
    switch (step) {
      case 'turn_left':
        return s.yaw > 0.7;
      case 'turn_right':
        return s.yaw < 0.3;
      case 'look_up':
        return s.pitch < 0.3;
      case 'smile':
        return s.smile > 0.6;
      case 'blink':
        if (phase === 0 && s.blink > 0.55) phase = 1;
        else if (phase === 1 && s.blink < 0.2) return true;
        return false;
    }
  };
}

export function captureJpeg(video: HTMLVideoElement, maxWidth = 640, quality = 0.82): string {
  const scale = Math.min(1, maxWidth / video.videoWidth);
  const c = document.createElement('canvas');
  c.width = Math.round(video.videoWidth * scale);
  c.height = Math.round(video.videoHeight * scale);
  c.getContext('2d')!.drawImage(video, 0, 0, c.width, c.height);
  return c.toDataURL('image/jpeg', quality);
}

export function distanceM(a: { lat: number; lng: number }, b: { lat: number; lng: number }) {
  const R = 6_371_000;
  const r = (x: number) => (x * Math.PI) / 180;
  const h = Math.sin(r(b.lat - a.lat) / 2) ** 2 + Math.cos(r(a.lat)) * Math.cos(r(b.lat)) * Math.sin(r(b.lng - a.lng) / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}
